import { useTranslation } from 'react-i18next';
import { useLoginPageViewModel } from '../hooks/useLoginPageViewModel';
import { usePageSEO } from '@/shared/hooks/usePageSEO';
import { ludokaLogo } from '@/assets';
import { PillButton } from '@/shared/components/PillButton';
import { createResponsiveTextStroke } from '@/styles/textStroke';

/**
 * Busy indicator for this page — in place of a provider label while a login is
 * in flight, and beside the bridge-polling copy. A login stays in flight from
 * the click until the session is fully resolved, which outlasts the Web3Auth
 * modal being on screen.
 */
function Spinner({ className = 'w-5 h-5 align-middle' }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={`inline-block border-2 border-white border-t-transparent rounded-full animate-spin ${className}`}
    />
  );
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { state, actions } = useLoginPageViewModel();
  const seoElement = usePageSEO({
    title: t('seo.login.title'),
    description: t('seo.login.description'),
    canonicalPath: '/login'
  });

  return (
    <div className="flex-1 w-full relative flex items-center justify-center">
      {seoElement}

      {/* Center content - Logo */}
      <img src={ludokaLogo} alt="Ludoka Logo" className="max-w-full max-h-full object-contain" />

      {/* Bottom - Login Buttons */}
      <div className="absolute bottom-40 left-1/2 -translate-x-1/2 w-full max-w-[280px] px-4">
        {/* Connect Text */}
        <h2
          className="font-mind-boggle text-[#F2F2F2] text-4xl text-center mb-4"
          style={createResponsiveTextStroke(2, 0.08)}
        >
          {t('login.connect')}
        </h2>

        {/* Error Message — show bridge errors everywhere, hide Web3Auth errors in embedded */}
        {state.error && (state.isBridgePolling || !state.isEmbeddedContext) && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg">
            <p className="text-red-400 text-sm text-center">{state.error}</p>
          </div>
        )}

        {state.isEmbeddedContext && !state.isBridgePolling ? (
          /* Embedded context - explain the browser login flow */
          <div className="flex flex-col gap-3 items-center">
            <p className="text-white/80 text-sm text-center mb-2">
              {t('login.embeddedInstruction')}
            </p>
            <PillButton
              variant="blue"
              onClick={actions.handleFacebookLogin}
              className="h-14 w-full"
              disabled={state.isLoading}
            >
              {state.isLoading ? <Spinner /> : 'FACEBOOK'}
            </PillButton>

            <PillButton
              variant="red"
              onClick={actions.handleGoogleLogin}
              className="h-14 w-full"
              disabled={state.isLoading}
            >
              {state.isLoading ? <Spinner /> : 'GOOGLE'}
            </PillButton>
          </div>
        ) : state.isBridgePolling ? (
          /* Bridge polling state - waiting for browser login */
          <div className="flex flex-col gap-3 items-center">
            <div className="flex items-center gap-2 mb-2">
              <Spinner className="w-4 h-4" />
              <p className="text-white text-sm">{t('login.waitingForBrowser')}</p>
            </div>
            <p className="text-white/60 text-xs text-center mb-2">
              {t('login.waitingForBrowserHint')}
            </p>
            <PillButton
              variant="blue"
              onClick={actions.cancelBridgePolling}
              className="h-14 w-full"
            >
              {t('login.cancel')}
            </PillButton>
          </div>
        ) : (
          /* Normal login buttons (non-embedded) */
          <div className="flex flex-col gap-3">
            <PillButton
              variant="blue"
              onClick={actions.handleFacebookLogin}
              className="h-14"
              disabled={state.isLoading}
            >
              {state.isLoading ? <Spinner /> : 'FACEBOOK'}
            </PillButton>

            <PillButton
              variant="red"
              onClick={actions.handleGoogleLogin}
              className="h-14"
              disabled={state.isLoading}
            >
              {state.isLoading ? <Spinner /> : 'GOOGLE'}
            </PillButton>
          </div>
        )}
      </div>
    </div>
  );
}
